import React, { useState, useEffect } from 'react';
import { Divider } from '../../components/sidebar/components/Divider';
import calendar from '../../assets/date.svg';
import ScrollArea from '../../components/common/scrollArea';
import plan from '../../assets/plan.svg';
import downloadIcon from '../../assets/download.svg';
import { useSubscriptionDays } from '@/hooks/useSubscriptionDays';
import { useOrganization } from '@/hooks/useOrganization';
import { plans } from '@/data/plans';
import apiHandler from '../../services/apiServices';
import { toast } from 'react-toastify';
import { usePayment } from '@/hooks/usePayment';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '../../lib/react-query';


type Variant = {
  id: string;
  attributes: {
    price: number;
    interval: string;
    product_id: number;
  };
};

type Order = {
  id: string;
  attributes: {
    product_name: string;
    status: string;
    created_at: string;
    order_id: number;
    variant_id: string;
  };
};

const getBillingType = (startDate: string | null, endDate: string | null): 'Monthly' | 'Yearly' => {
  if (!startDate || !endDate) return 'Monthly'; // Default to monthly if dates aren't available
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Calculate the difference in months
  const diffInMonths = (end.getFullYear() - start.getFullYear()) * 12 + 
    (end.getMonth() - start.getMonth());
    
  return diffInMonths >= 12 ? 'Yearly' : 'Monthly';
};

const fetchOrders = async () => {
  const response = await apiHandler.get('/payment/list-orders');
  return response.data.orders;
};

const fetchVariants = async () => {
  const response = await apiHandler.get('/payment/variants');
  return response.data;
};

const BillingPage = () => {
  const { daysRemaining } = useSubscriptionDays();
  const { organization } = useOrganization();

  // Find current plan details
  const currentPlan = plans.find(p => p.id === organization?.current_plan);
  
  // Determine billing type
  const billingType = getBillingType(
    organization?.subscription_start_date ?? null,
    organization?.subscription_end_date ?? null
  );

  const { data: orders, error: ordersError, refetch: refetchOrders } = useQuery({
    queryKey: ['orders'],
    queryFn: fetchOrders,
  });

  const cancelSubscription = useMutation({
    mutationFn: async (orderId: number) => {
      const response = await apiHandler.post(`/payment/cancel-subscription?subscription_id=${orderId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', 'orders'] });
      refetchOrders();
    },
    onError: (error) => {
      console.error('Error cancelling subscription:', error);
    }
  });

  const { data: variants, error: variantsError } = useQuery({
    queryKey: ['variants'],
    queryFn: fetchVariants,
    enabled: !!orders, // Fetch variants only after orders are fetched
  });

  if (ordersError || variantsError) {
    console.error('Error fetching data:', ordersError || variantsError);
  }

  const getOrderPrice = (variantId: string): string => {
    const variant = variants?.find((v: Variant) => v.id === variantId.toString());
    console.log('debug:variant',{variant, variantId});
    if (!variant) return '$0.00';
    
    // Access the price from the attributes
    const priceInCents = variant.attributes.price;
    
    // Convert cents to dollars
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const getMostRecentActiveSubscription = (orders: Order[]) => {
    return orders
      .filter(order => order.attributes.status === 'active')
      .sort((a, b) => {
        const dateA = new Date(a.attributes.created_at);
        const dateB = new Date(b.attributes.created_at);
        return dateB.getTime() - dateA.getTime();
      })[0];
  };

  const handleCancelSubscription = async (orderId: string) => {
    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      try {
        await cancelSubscription.mutate(parseInt(orderId));
        toast.success('Subscription cancelled successfully');
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        toast.error('Failed to cancel subscription. Please try again.');
      }
    }
  };
  console.log({orders,variants});

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col w-[762px] text-[Nunito Sans]">
        {/* Header */}
        <div className="flex items-start mt-5 mb-2">
          <div className="text-start text-sm font-light text-[#75777C]">
            Workspace settings
          </div>
          <div className="text-start text-sm font-light text-[#222222] pl-2">
            / Billing
          </div>
        </div>

        <ScrollArea
          maxHeight="calc(100vh - 80px)"
        >
          <div className="flex flex-col">
            <div className="flex flex-col mb-7">
              <div className="flex flex-col mt-5 text-left gap-[2px]">
                <div className="text-xl font-semibold text-[#222222] ">Billing</div>
                <div className="text-sm font-light text-[#75777C] ">
                  Update your payment information or switch plans according to your needs
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1]" />

            {/* Trial Notice */}
            {organization?.current_plan === 'free' && daysRemaining > 0 && (
              <div className="flex justify-between items-center bg-gray-50 px-[14px] py-[8px] rounded-[8px] my-7">
                <div className="flex items-center gap-2">
                  <img src={calendar} alt="Icon" className="w-[14px] h-[14px]" />
                  <span className="text-sm font-light text-[#5C5E63]">
                    There are {daysRemaining} days left on your subscription
                  </span>
                </div>
              </div>
            )}

            {/* Plan Section */}
            <div className="flex flex-col pt-7 items-start">
              <h2 className="text-xl font-semibold text-[#222222] mb-6 text-left">Plan</h2>
              
              <div className="w-full grid grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="flex flex-col text-left">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-[42px] h-[42px] bg-[#E8FFF0] rounded-lg flex items-center justify-center">
                      <img src={plan} alt="Icon" className="w-[40px] h-[40px]" />
                    </div>
                    <div className="text-left">
                      <span className="font-semibold text-[#222222]">{currentPlan?.name || 'Free'}</span>
                      <span className="ml-2 text-[12px] items-center justify-center font-light text-[#12692B] bg-[#DAFEE4] px-[6px] py-[2px] border border-[#81F4A1] rounded-[8px]">
                        {billingType}
                      </span>
                    </div>
                  </div>
                  <div className="text-[#75777C] text-sm text-left font-light ">
                    $ {currentPlan?.price || 0}.00 per seat/ month
                  </div>
                </div>

                {/* Right Column */}
                <div className="flex flex-col text-left">
                  <h3 className="font-normal mb-1 text-[#222222] text-left ">Billing period</h3>
                  <p className="text-[#75777C] text-sm text-left font-light ">
                    Monthly ( starts {new Date(organization?.subscription_start_date || Date.now()).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })} )
                  </p>
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] my-5" />


            {/* <div className="flex flex-col mb-7">
              <div className="flex justify-between items-center mb-6">
                <div className="text-left">
                  <h2 className="text-xl font-semibold text-[#222222] text-left">Billing details</h2>
                  <p className="text-sm text-[#75777C] text-left">Manage your payment methods and billing information</p>
                </div>
              </div>

              <div className="w-full grid grid-cols-2 gap-8">
                <div className="flex flex-col space-y-6">
                  <div className="text-left text-sm">
                    <h3 className="font-normal mb-1 text-[#222222] text-left">Billing email</h3>
                    <p className="text-[#5C5E63] text-sm text-left">nirupma@clodexa.com</p>
                  </div>
                  <div className="text-left text-sm">
                    <h3 className="font-normal mb-1 text-[#222222] text-left">Company name</h3>
                    <p className="text-[#5C5E63] text-sm text-left">Clodexa</p>
                  </div>
                  <div className="text-left text-sm">
                    <h3 className="font-normal mb-1 text-[#222222] text-left">Address</h3>
                    <p className="text-[#5C5E63] text-sm text-left">India</p>
                  </div>
                  <div className="text-left text-sm">
                    <h3 className="font-normal mb-1 text-[#222222] text-left">GST number</h3>
                    <p className="text-[#5C5E63] text-sm text-left">Not provided</p>
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-col text-left text-sm gap-2">
                    <h3 className="font-normal text-[#222222] text-left">Payment methods</h3>
                    <button className="text-[12px] font-light w-fit text-[#222222] text-left rounded-[8px] p-2 shadow-[0_0_4px_0_#E0E0E0]">
                      Add payment method
                    </button>
                  </div>
                </div> 
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] mb-7" /> */}

            {/* History Section */}
            <div className="flex flex-col items-start justify-center">
              <div className="flex flex-col items-start justify-start">
                <h2 className="text-xl font-semibold mb-2 text-[#222222]">History</h2>
                <p className="text-sm text-[#5C5E63] mb-6">
                  View and track your past invoices and payment history
                </p>
              </div>
              {/* History Table */}
              <div className="w-full rounded-lg">
                {/* Table Header */}
                <div className="grid grid-cols-5">
                  <div className="py-3 text-left  text-sm font-normal text-[#75777C]">
                    Reference
                  </div>
                  <div className="py-3 text-left  text-sm font-normal text-[#75777C]">
                    Total incl. tax
                  </div>
                  <div className="py-3 text-left  text-sm font-normal text-[#75777C]">
                    Status
                  </div>
                  <div className="py-3 text-left  text-sm font-normal text-[#75777C]">
                    Date
                  </div>
                </div>

                {/* Table Body */}
                <div className="bg-white">
                  {(orders ?? []).map((order: Order) => (
                    <div key={order.id} className={`grid grid-cols-5 px-2 ${order.attributes.status === 'active' ? ' bg-[#F5F5F5]' : ''}`}>
                      <div className="py-3 text-left text-sm text-[#5C5E63]">
                        {order.attributes.order_id}
                      </div>
                      <div className="py-3 text-left text-sm text-[#5C5E63]">
                        {getOrderPrice(order?.attributes?.variant_id)}
                      </div>
                      <div className="py-3 text-left text-sm text-[#5C5E63]">
                        {order.attributes.status}
                      </div>
                      <div className="py-3 text-left text-sm text-[#5C5E63] flex items-center gap-4">
                        {new Date(order.attributes.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {/* <button className="text-[#5D90D4] hover:underline">
                          <img src={downloadIcon} alt="Download CSV" className="w-[14px] h-[14px] text-[#5C5E63]" />
                        </button> */}
                      </div>
                      {
                        order.attributes.status === 'active' && (
                          
                          <button 
                            onClick={() => handleCancelSubscription(order.id)}
                            className={`text-[12px] font-light w-fit text-left rounded-[8px] p-2 shadow-[0_0_4px_0_#E0E0E0]${
                              cancelSubscription.isPending 
                                ? 'bg-[#F5F5F5] text-[#222222] cursor-wait' 
                                : 'bg-[#FFFFFF] text-[#222222] hover:bg-[#F5F5F5]'
                            }`}
                          >
                            {cancelSubscription.isPending ? 'Cancelling...' : 'Cancel subscription'}
                            </button>
                          
                        )
                      }
                    </div>
                  ))}
                </div>
              </div>

              {/* Cancel Subscription Button */}
              
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default BillingPage;
